// Mock data for HC Assistant
//
// NOTE: This file is for development reference only and is NOT used at runtime
// for authentication, user data, or any sensitive operation. All real data and
// auth tokens flow through the FastAPI backend via /app/frontend/src/api.js.
// The localStorage references in this file are legacy placeholders for the
// initial mock scaffold; the production app uses JWT tokens managed by api.js.

export const CATEGORIES = [
  {
    id: 'busta-paga',
    title: 'Busta Paga',
    description: 'Chiarimenti su stipendio, trattenute e voci della busta paga',
    icon: 'Receipt',
    color: 'blue',
    prompts: [
      'Cosa significa la voce TFR nella mia busta paga?',
      'Come viene calcolata la tredicesima?',
      'Quali sono le trattenute IRPEF?'
    ]
  },
  {
    id: 'ferie',
    title: 'Ferie e Permessi',
    description: 'Richieste ferie, permessi retribuiti e ROL',
    icon: 'Palmtree',
    color: 'teal',
    prompts: [
      'Come posso richiedere le ferie estive?',
      'Quanti giorni di permesso retribuito ho disponibili?',
      'Quali sono le regole per i ROL?'
    ]
  },
  {
    id: 'ccnl',
    title: 'CCNL',
    description: 'Domande sul Contratto Collettivo Nazionale di Lavoro',
    icon: 'FileText',
    color: 'indigo',
    prompts: [
      'Qual è il mio livello di inquadramento?',
      'Cosa prevede il CCNL per gli straordinari?',
      'Quali sono i miei diritti sindacali?'
    ]
  },
  {
    id: 'policy',
    title: 'Policy Aziendale',
    description: 'Regolamenti interni, smart working e codice etico',
    icon: 'Shield',
    color: 'slate',
    prompts: [
      'Qual è la policy sullo smart working?',
      'Come funziona il rimborso spese?',
      'Dove trovo il codice etico aziendale?'
    ]
  }
];

export const MOCK_CHAT_HISTORY = [
  {
    id: 'chat-1',
    title: 'Calcolo tredicesima',
    category: 'busta-paga',
    updatedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    messages: [
      { role: 'user', content: 'Come viene calcolata la tredicesima?' },
      { role: 'assistant', content: 'La tredicesima corrisponde a una mensilità aggiuntiva ed è calcolata come 1/12 della retribuzione annuale lorda...' }
    ]
  },
  {
    id: 'chat-2',
    title: 'Richiesta ferie agosto',
    category: 'ferie',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    messages: [
      { role: 'user', content: 'Quanti giorni di ferie ho accumulato?' },
      { role: 'assistant', content: 'In base al CCNL applicato, hai diritto a 26 giorni lavorativi di ferie all\'anno...' }
    ]
  },
  {
    id: 'chat-3',
    title: 'Smart working policy',
    category: 'policy',
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    messages: []
  }
];

export const MOCK_RESPONSES = {
  'busta-paga': 'La busta paga contiene diverse voci obbligatorie come la retribuzione lorda, le trattenute fiscali (IRPEF), i contributi previdenziali (INPS) e il netto in busta. Per chiarimenti specifici su una voce, indicami quale vuoi approfondire.',
  'ferie': 'Per richiedere le ferie, accedi al portale HR aziendale nella sezione "Presenze". Compila il modulo indicando il periodo desiderato e invialo al tuo responsabile. Secondo il CCNL, hai diritto a 26 giorni lavorativi annui.',
  'ccnl': 'Il Contratto Collettivo Nazionale di Lavoro regola rapporti di lavoro, orari, retribuzione minima, ferie, malattia e preavviso. Posso fornirti dettagli specifici su qualsiasi aspetto del CCNL applicato alla tua azienda.',
  'policy': 'Le policy aziendali regolano comportamenti, smart working, rimborsi, privacy e codice etico. Puoi consultare tutti i documenti ufficiali nell\'area riservata del portale interno.',
  'default': 'Sono HC Assistant, il tuo supporto HR. Posso aiutarti con domande su busta paga, ferie, CCNL e policy aziendali. Come posso assisterti oggi?'
};

// ============= REQUESTS (Vacation + Sickness) =============

export const VACATION_TYPES = [
  { value: 'ferie', label: 'Ferie' },
  { value: 'permesso', label: 'Permesso retribuito' },
  { value: 'rol', label: 'ROL (Riduzione Orario Lavoro)' },
  { value: 'congedo', label: 'Congedo straordinario' }
];

export const REQUEST_STATUS = {
  pending: { label: 'In attesa', color: 'amber' },
  approved: { label: 'Approvata', color: 'emerald' },
  rejected: { label: 'Rifiutata', color: 'red' }
};

const VACATION_KEY = 'aihc_vacation_requests';
const SICKNESS_KEY = 'aihc_sickness_protocols';

// Seed sample data on first load (for HR demo)
const seedIfEmpty = () => {
  if (!localStorage.getItem(VACATION_KEY)) {
    const sample = [
      {
        id: 'v-demo-1',
        employeeEmail: 'giulia.bianchi@azienda.it',
        employeeName: 'Giulia Bianchi',
        type: 'ferie',
        startDate: new Date(Date.now() + 86400000 * 14).toISOString().slice(0, 10),
        endDate: new Date(Date.now() + 86400000 * 21).toISOString().slice(0, 10),
        days: 6,
        notes: 'Vacanza estiva con famiglia',
        attachments: [],
        status: 'pending',
        createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
        reviewedAt: null,
        reviewNotes: ''
      },
      {
        id: 'v-demo-2',
        employeeEmail: 'luca.verdi@azienda.it',
        employeeName: 'Luca Verdi',
        type: 'permesso',
        startDate: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
        endDate: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
        days: 1,
        notes: 'Visita medica',
        attachments: [],
        status: 'pending',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        reviewedAt: null,
        reviewNotes: ''
      }
    ];
    localStorage.setItem(VACATION_KEY, JSON.stringify(sample));
  }
  if (!localStorage.getItem(SICKNESS_KEY)) {
    const sample = [
      {
        id: 's-demo-1',
        employeeEmail: 'marco.neri@azienda.it',
        employeeName: 'Marco Neri',
        protocolNumber: '12345678901',
        startDate: new Date(Date.now() - 86400000 * 2).toISOString().slice(0, 10),
        endDate: new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 10),
        doctor: 'Dr. Rossi Francesco',
        notes: 'Influenza stagionale',
        attachments: [],
        createdAt: new Date(Date.now() - 86400000 * 2).toISOString()
      }
    ];
    localStorage.setItem(SICKNESS_KEY, JSON.stringify(sample));
  }
};

export const getVacationRequests = () => {
  seedIfEmpty();
  try { return JSON.parse(localStorage.getItem(VACATION_KEY) || '[]'); } catch { return []; }
};

export const saveVacationRequests = (list) => {
  localStorage.setItem(VACATION_KEY, JSON.stringify(list));
};

export const getSicknessProtocols = () => {
  seedIfEmpty();
  try { return JSON.parse(localStorage.getItem(SICKNESS_KEY) || '[]'); } catch { return []; }
};

export const saveSicknessProtocols = (list) => {
  localStorage.setItem(SICKNESS_KEY, JSON.stringify(list));
};

export const calculateBusinessDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (end < start) return 0;
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

// ============= WORKPLACES & ATTENDANCE =============

export const WORKPLACE_RADIUS_M = 100; // meters
const WORKPLACES_KEY = 'aihc_workplaces';
const ATTENDANCE_KEY = 'aihc_attendance';

export const ATTENDANCE_TYPES = {
  entrata: { label: 'Entrata', icon: 'LogIn', color: 'emerald' },
  pausa_inizio: { label: 'Inizio pausa', icon: 'Coffee', color: 'amber' },
  pausa_fine: { label: 'Fine pausa', icon: 'Coffee', color: 'blue' },
  uscita: { label: 'Uscita', icon: 'LogOut', color: 'slate' }
};

const DEFAULT_WORKPLACES = [
  {
    id: 'wp-hc-bottanuco',
    name: 'HC Bottanuco',
    address: 'Via Ginevra 4, 24040 Bottanuco (BG) Italia',
    lat: 45.636212804071896,
    lng: 9.513904152810497,
    radius: 100,
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'wp-hc-suarez',
    name: 'HC Suarez',
    address: 'Via G. Battista Pergolesi 1, 20811 Cesano Maderno (MB), Italia',
    lat: 45.622763988556386,
    lng: 9.140971324138837,
    radius: 100,
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 'wp-hc-valencia',
    name: 'HC Valencia',
    address: 'Calle Valencia 29, 46120 Alboraya (VLT) Spagna',
    lat: 39.49824770637382,
    lng: -0.35352360326154386,
    radius: 100,
    active: true,
    createdAt: new Date().toISOString()
  }
];

const seedWorkplacesIfEmpty = () => {
  let current = [];
  try { current = JSON.parse(localStorage.getItem(WORKPLACES_KEY) || '[]'); } catch { current = []; }
  if (current.length === 0) {
    localStorage.setItem(WORKPLACES_KEY, JSON.stringify(DEFAULT_WORKPLACES));
    return;
  }
  // Merge: add any default workplace that's missing by id
  const existingIds = new Set(current.map(w => w.id));
  const missing = DEFAULT_WORKPLACES.filter(w => !existingIds.has(w.id));
  if (missing.length > 0) {
    localStorage.setItem(WORKPLACES_KEY, JSON.stringify([...current, ...missing]));
  }
};

export const getWorkplaces = () => {
  seedWorkplacesIfEmpty();
  try { return JSON.parse(localStorage.getItem(WORKPLACES_KEY) || '[]'); } catch { return []; }
};

export const saveWorkplaces = (list) => {
  localStorage.setItem(WORKPLACES_KEY, JSON.stringify(list));
};

export const getAttendance = () => {
  try { return JSON.parse(localStorage.getItem(ATTENDANCE_KEY) || '[]'); } catch { return []; }
};

export const saveAttendance = (list) => {
  localStorage.setItem(ATTENDANCE_KEY, JSON.stringify(list));
};

// Haversine distance formula - returns distance in meters
export const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth radius in meters
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Find nearest active workplace within radius; return { workplace, distance } or null
export const findNearestWorkplace = (lat, lng, workplaces = null) => {
  const list = (workplaces || getWorkplaces()).filter(w => w.active);
  let best = null;
  for (const wp of list) {
    const d = haversineDistance(lat, lng, wp.lat, wp.lng);
    if (!best || d < best.distance) {
      best = { workplace: wp, distance: d };
    }
  }
  if (!best) return null;
  const radius = best.workplace.radius || WORKPLACE_RADIUS_M;
  return { ...best, inRange: best.distance <= radius, radius };
};

// Get today's attendance events for an employee (sorted by time asc)
export const getTodayAttendance = (email) => {
  const today = new Date().toISOString().slice(0, 10);
  return getAttendance()
    .filter(a => a.employeeEmail === email && a.timestamp.startsWith(today))
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
};

// Determine current attendance status based on today's events
export const getAttendanceStatus = (events) => {
  if (!events || events.length === 0) return 'none'; // not clocked in
  const last = events[events.length - 1];
  if (last.type === 'entrata') return 'working';
  if (last.type === 'pausa_inizio') return 'on_break';
  if (last.type === 'pausa_fine') return 'working';
  if (last.type === 'uscita') return 'done';
  return 'none';
};

// Determine next allowed actions given current events
export const getAllowedActions = (events) => {
  const status = getAttendanceStatus(events);
  switch (status) {
    case 'none':   return { entrata: true,  pausa_inizio: false, pausa_fine: false, uscita: false };
    case 'working':return { entrata: false, pausa_inizio: true,  pausa_fine: false, uscita: true  };
    case 'on_break':return { entrata: false, pausa_inizio: false, pausa_fine: true, uscita: false };
    case 'done':   return { entrata: false, pausa_inizio: false, pausa_fine: false, uscita: false };
    default: return { entrata: false, pausa_inizio: false, pausa_fine: false, uscita: false };
  }
};

export const mockSendMessage = async (message, category = null) => {
  await new Promise(r => setTimeout(r, 800 + Math.random() * 800));
  const key = category || 'default';
  const base = MOCK_RESPONSES[key] || MOCK_RESPONSES.default;
  return `${base}\n\nIn merito alla tua domanda: "${message}", posso fornirti dettagli aggiuntivi se mi indichi il contesto specifico.`;
};
